import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import AppShell from './components/AppShell';
import LoadingScreen from './components/LoadingScreen';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/tenant/DashboardPage';
import ScreensPage from './pages/tenant/ScreensPage';
import MediaPage from './pages/tenant/MediaPage';
import PlaylistsPage from './pages/tenant/PlaylistsPage';
import LayoutsPage from './pages/tenant/LayoutsPage';
import SchedulerPage from './pages/tenant/SchedulerPage';
import WidgetsPage from './pages/tenant/WidgetsPage';
import AnalyticsPage from './pages/tenant/AnalyticsPage';
import UsersPage from './pages/tenant/UsersPage';
import SubscriptionPage from './pages/tenant/SubscriptionPage';
import SettingsPage from './pages/tenant/SettingsPage';
import SuperDashboardPage from './pages/super/SuperDashboardPage';
import TenantsPage from './pages/super/TenantsPage';
import PlansPage from './pages/super/PlansPage';
import SubscriptionsPage from './pages/super/SubscriptionsPage';
import SuperUsersPage from './pages/super/SuperUsersPage';
import SuperSettingsPage from './pages/super/SuperSettingsPage';
import PaymentGatewaysPage from './pages/super/PaymentGatewaysPage';
import SubscriptionGate from './components/SubscriptionGate';

function Protected({ superOnly = false, tenantOnly = false, children }) {
  const { authUser, profile, loading, isSuperAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!authUser || !profile) {
    return <Navigate to={superOnly ? '/superadmin/login' : '/tenant/login'} replace />;
  }
  if (superOnly && !isSuperAdmin) return <Navigate to="/tenant/dashboard" replace />;
  if (tenantOnly && isSuperAdmin) return <Navigate to="/superadmin/dashboard" replace />;
  return children;
}

function SubscriptionProtected({ children }) {
  return <SubscriptionGate routeMode>{children}</SubscriptionGate>;
}

function HomeRedirect() {
  const { authUser, profile, loading, isSuperAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!authUser || !profile) return <Navigate to="/tenant/login" replace />;
  return <Navigate to={isSuperAdmin ? '/superadmin/dashboard' : '/tenant/dashboard'} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Dedicated login URLs */}
      <Route path="/tenant/login" element={<LoginPage portal="tenant" />} />
      <Route path="/superadmin/login" element={<LoginPage portal="super" />} />
      <Route path="/tenant/register" element={<RegisterPage />} />

      {/* Backward-compatible aliases */}
      <Route path="/login" element={<Navigate to="/tenant/login" replace />} />
      <Route path="/register" element={<Navigate to="/tenant/register" replace />} />
      <Route path="/app/*" element={<Navigate to="/tenant/dashboard" replace />} />
      <Route path="/super/*" element={<Navigate to="/superadmin/dashboard" replace />} />
      <Route path="/" element={<HomeRedirect />} />

      <Route path="/tenant" element={<Protected tenantOnly><AppShell mode="tenant" /></Protected>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="screens" element={<SubscriptionProtected><ScreensPage /></SubscriptionProtected>} />
        <Route path="media" element={<SubscriptionProtected><MediaPage /></SubscriptionProtected>} />
        <Route path="playlists" element={<SubscriptionProtected><PlaylistsPage /></SubscriptionProtected>} />
        <Route path="layouts" element={<SubscriptionProtected><LayoutsPage /></SubscriptionProtected>} />
        <Route path="scheduler" element={<SubscriptionProtected><SchedulerPage /></SubscriptionProtected>} />
        <Route path="widgets" element={<SubscriptionProtected><WidgetsPage /></SubscriptionProtected>} />
        <Route path="analytics" element={<SubscriptionProtected><AnalyticsPage /></SubscriptionProtected>} />
        <Route path="users" element={<SubscriptionProtected><UsersPage /></SubscriptionProtected>} />
        {/* Subscription and settings stay accessible after expiry so a tenant can renew or get help. */}
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="/superadmin" element={<Protected superOnly><AppShell mode="super" /></Protected>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<SuperDashboardPage />} />
        <Route path="companies" element={<TenantsPage />} />
        <Route path="tenants" element={<Navigate to="../companies" replace />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="payment-gateways" element={<PaymentGatewaysPage />} />
        <Route path="users" element={<SuperUsersPage />} />
        <Route path="settings" element={<SuperSettingsPage />} />
      </Route>

      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}
